import { Controller } from '@hotwired/stimulus';
import { afterNextRender } from '../../utilities/timing';

const templates: Record<string, string> = {
  '+1': '___-___-____',
  '+44': '____ ______',
  '+61': '4__ ___ ___',
};

export default class extends Controller {
  private readonly digitPlaceholder = '_';

  static readonly targets = ['input', 'country'];

  declare inputTarget: HTMLInputElement;
  declare countryTarget: HTMLSelectElement;
  declare hasCountryTarget: boolean;

  private template: string = '';
  private lastClipboardText: string = '';

  get strippedValue() {
    return getDigitsOnly(this.inputTarget.value.slice(0, this.template.length));
  }

  get templatePhoneSize() {
    return this.template ?
      this.template.length -
      this.template.replaceAll(this.digitPlaceholder, '').length +
      this.template.replaceAll(/\D/g, '').length 
    : 0;
  }

  get country() {
    return this.countryTarget.value;
  }

  initialize() {
    this.setTemplate();
  }

  connect() {
    this.inputTarget.addEventListener('input', this.onInput);
    this.inputTarget.addEventListener('paste', this.onPaste);
    this.countryTarget.addEventListener('change', this.setTemplate);
  }

  disconnect() {
    this.inputTarget.removeEventListener('input', this.onInput);
    this.inputTarget.removeEventListener('paste', this.onPaste);
    this.countryTarget.removeEventListener('change', this.setTemplate);
  }

  private onPaste = (event: ClipboardEvent) => {
    this.lastClipboardText = event.clipboardData?.getData('text') || '';
  }

  private onInput = (event: Event) => {
    const inputEvent = event as InputEvent;
    const target = inputEvent.target as HTMLInputElement;
    let startingCursor =
      target.selectionStart ?? this.inputTarget.value.length;

    // since we might have enforced digits within template, this would make copy/paste more friendly
    // as we allow users to replace the entire input value including starting digits
    if (inputEvent.inputType === 'insertFromPaste') {
      const digitsPhone = getDigitsOnly(this.lastClipboardText);
      if (this.templatePhoneSize <= digitsPhone.length) {
        target.value = digitsPhone;
      }
    }

    if (this.template) {
      startingCursor = this.bypassDelimiterOnBackwardDelete(inputEvent, startingCursor);
      startingCursor = this.bypassLeadingDigitOnEdit(inputEvent, startingCursor);
      this.adjustInputValueByMask();
      this.adjustCursorPosition(target, startingCursor);
    }
  };

  private bypassLeadingDigitOnEdit = (inputEvent: InputEvent, cursorPosition: number): number => {
    const leadingDigits = /^(\d+)/.exec(this.template)?.[0];
    const leadingDigitsSize = leadingDigits?.length ?? 0;
    let updatedCursor = cursorPosition;

    if (cursorPosition <= leadingDigitsSize && inputEvent.inputType == 'insertText') {
      const { value } = this.inputTarget;
      const inputKeyValue = value.substring(cursorPosition - 1, cursorPosition);

      this.inputTarget.value = `${leadingDigits}${inputKeyValue}${value.substring(leadingDigitsSize + 1)}`;
      updatedCursor += leadingDigitsSize;
    }

    return updatedCursor;
  }

  // this will allow users to edit at any cursor position
  private bypassDelimiterOnBackwardDelete = (inputEvent: InputEvent, cursorPosition: number): number => {
    if ('deleteContentBackward' !== inputEvent.inputType) {
      return cursorPosition;
    }
  
    const maskItemOnCurrentPosition = this.template[cursorPosition];
    const isDigit = /^[0-9]$/.test(maskItemOnCurrentPosition);
    const isDelimiter = maskItemOnCurrentPosition !== this.digitPlaceholder && !isDigit;
    let updatedCursor = cursorPosition;

    if (isDelimiter) {
      while (this.template[updatedCursor] != this.digitPlaceholder && updatedCursor > 0) {
        updatedCursor -= 1;
      }

      if (cursorPosition != updatedCursor) {
        const { value } = this.inputTarget;
        this.inputTarget.value = `${value.substring(0, updatedCursor)}${value.substring(cursorPosition, value.length)}`;
      }
    }

    return updatedCursor;
  }

  private adjustInputValueByMask = () => {
    this.inputTarget.value = maskPhoneNumber(
      this.template,
      this.strippedValue,
      this.digitPlaceholder,
    );
  }

  private setTemplate = () => {
    this.template = templates[this.country];

    if (this.template) {
      this.adjustInputValueByMask();
      this.inputTarget.maxLength = this.template.length;
      this.inputTarget.pattern = `(\\d\\D*){${this.templatePhoneSize}}`;
    }
  };

  private adjustCursorPosition(
    target: HTMLInputElement,
    startingCursor: number,
  ) {
    const newPosition = findNewPositionByMaskAndIndex(
      this.template,
      startingCursor,
      this.digitPlaceholder,
    );
    setCursorPosition(target, newPosition);
  }
}

function findNewPositionByMaskAndIndex(
  template: string,
  currentIndex: number,
  digitPlaceholder: string,
) {
  let newPosition = currentIndex;
  for (let index = currentIndex; index < template.length; index++) {
    if (template[index] === digitPlaceholder) {
      break;
    } else {
      newPosition += 1;
    }
  }

  return newPosition;
}

function maskPhoneNumber(
  mask: string,
  strippedValue: string,
  digitPlaceholder: string
): string {
  const result: string[] = [];
  const parsedValue = strippedValue;

  let parsedValueIndex = 0;
  for (const maskDigit of mask) {
    const isDigitMask = /^[0-9]$/.test(maskDigit);
    const isDelimeter = maskDigit !== digitPlaceholder && !isDigitMask;
  
    if (isDelimeter) {
      result.push(maskDigit);
    } else if (isDigitMask) {
      result.push(maskDigit);
      parsedValueIndex += 1;
    } else if (parsedValueIndex < parsedValue.length) {
      result.push(parsedValue[parsedValueIndex]);
      parsedValueIndex += 1;
    } else {
      break;
    }
  }

  return result.join('');
}

function getDigitsOnly(value: string) {
  return value ? value.replaceAll(/\D/g, '') : '';
}

function setCursorPosition(element: HTMLInputElement, position: number) {
  afterNextRender(() => element.setSelectionRange(position, position));
}
